import React, { createContext, useContext, useMemo } from 'react';
import { MessageDetailsInterface, MessageReplyInterface } from '../../../interfaces/chatMessageInterface';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { UserModel } from '../../../mobx/models/user';

// Shared interface definitions

export interface MessageFeedEnvironment {
  /** Rendered inside the navbar messages panel */
  isNavbarMessage: boolean;
  /** Rendered on the project detail page */
  isProjectDetail: boolean;
  /** Rendered in the project message detail view */
  isProjectMessageDetail: boolean;
  /** Rendered in a generic message context */
  isMessage: boolean;
}

export interface FileSelectionState {
  /** Whether the attachment panel (sidebar/drawer) is open */
  attachment: boolean;
  setAttachment: (value: boolean) => void;
  /** Whether the mobile full-screen file picker is active */
  selectFile: boolean;
  setSelectFile: (value: boolean) => void;
}

// MessageActionsContext — stable callbacks that never change on state updates
export interface MessageActionsContextValue {
  onReactOnMessage: (emojiData: { emoji: string; imageUrl?: string }, item: MessageDetailsInterface) => Promise<void>;
  onClickReply: (item: MessageDetailsInterface) => void;
  editMessageData: (messageData: MessageDetailsInterface) => Promise<void>;
  openDeleteMessageModal: (itemId: string) => void;
  handleOpenChange: (itemId: string) => void;
  setSelectedMessage: (itemId: string | null) => void;
  setOpenEmoji: (value: boolean) => void;
  onClickHandleRichtextClick: (event: React.MouseEvent, item: MessageDetailsInterface) => Promise<void>;
  onClickHandleRefreshFigma: (item: MessageDetailsInterface, figmaURL: string) => Promise<void>;
  onClickFile: (file: CommentFilesInterface) => void;
  getReplyMessage: (reply: MessageReplyInterface | undefined) => string | undefined;
  onClickUserPopup: (user: UserModel) => void;
}

// MessageSelectionContext — volatile selection / UI state
export interface MessageSelectionContextValue {
  selectedMesaage: string | null;
  openEmoji: boolean;
  deletedMessageId: string | null;
  emojiLoading: boolean;
  figmaRefreshLoading: string | null;
  emojiButtonRef: React.MutableRefObject<Array<React.RefObject<HTMLDivElement>>>;
}

// MessageEnvironmentContext — stable environment / user / workspace info
export interface MessageEnvironmentContextValue {
  mobile: boolean;
  isMessage: boolean | undefined;
  userUseEmoji: string[] | null;
  workspace: { users?: UserModel[] } | null;
  currentUser: UserModel | null;
  isProjectMessageDetail: boolean | undefined;
}

// Legacy MessageBubbleShared — kept for backward compatibility during migration.
// New consumers should use the three focused contexts above instead.
/** @deprecated Use MessageActionsContext, MessageSelectionContext, and MessageEnvironmentContext instead. */
export interface MessageBubbleShared
  extends MessageActionsContextValue,
    MessageSelectionContextValue,
    MessageEnvironmentContextValue {}

// Root context (environment + fileSelection only)

interface MessageFeedContextValue {
  environment: MessageFeedEnvironment;
  fileSelection: FileSelectionState;
}

const MessageFeedContext = createContext<MessageFeedContextValue | null>(null);

export const useMessageFeedContext = (): MessageFeedContextValue => {
  const ctx = useContext(MessageFeedContext);
  if (!ctx) {
    throw new Error('useMessageFeedContext must be used within a MessageFeedProvider');
  }
  return ctx;
};

export const useMessageFeedEnvironment = (): MessageFeedEnvironment => {
  return useMessageFeedContext().environment;
};

export const useFileSelection = (): FileSelectionState => {
  return useMessageFeedContext().fileSelection;
};

// MessageActionsContext
const MessageActionsContext = createContext<MessageActionsContextValue | null>(null);

export const useMessageActionsContext = (): MessageActionsContextValue => {
  const ctx = useContext(MessageActionsContext);
  if (!ctx) {
    throw new Error('useMessageActionsContext must be used within a MessageFeedProvider');
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// MessageSelectionContext
const MessageSelectionContext = createContext<MessageSelectionContextValue | null>(null);

export const useMessageSelectionContext = (): MessageSelectionContextValue => {
  const ctx = useContext(MessageSelectionContext);
  if (!ctx) {
    throw new Error('useMessageSelectionContext must be used within a MessageFeedProvider');
  }
  return ctx;
};

// MessageEnvironmentContext
const MessageEnvironmentContext = createContext<MessageEnvironmentContextValue | null>(null);

export const useMessageEnvironmentContext = (): MessageEnvironmentContextValue => {
  const ctx = useContext(MessageEnvironmentContext);
  if (!ctx) {
    throw new Error('useMessageEnvironmentContext must be used within a MessageFeedProvider');
  }
  return ctx;
};

// Legacy hook — composes all three focused contexts for backward compatibility.
// @deprecated Prefer the three focused hooks above.

/** @deprecated Use useMessageActionsContext, useMessageSelectionContext, or useMessageEnvironmentContext instead. */
export const useMessageBubbleShared = (): MessageBubbleShared => {
  const actions = useMessageActionsContext();
  const selection = useMessageSelectionContext();
  const environment = useMessageEnvironmentContext();
  return useMemo(() => ({ ...actions, ...selection, ...environment }), [actions, selection, environment]);
};

// Provider
interface MessageFeedProviderProps {
  environment: MessageFeedEnvironment;
  fileSelection: FileSelectionState;
  messageActions: MessageActionsContextValue;
  messageSelection: MessageSelectionContextValue;
  messageEnvironment: MessageEnvironmentContextValue;
  children: React.ReactNode;
}

export const MessageFeedProvider: React.FC<MessageFeedProviderProps> = ({
  environment,
  fileSelection,
  messageActions,
  messageSelection,
  messageEnvironment,
  children
}) => {
  const rootValue = useMemo(() => ({ environment, fileSelection }), [environment, fileSelection]);

  return (
    <MessageFeedContext.Provider value={rootValue}>
      <MessageActionsContext.Provider value={messageActions}>
        <MessageSelectionContext.Provider value={messageSelection}>
          <MessageEnvironmentContext.Provider value={messageEnvironment}>{children}</MessageEnvironmentContext.Provider>
        </MessageSelectionContext.Provider>
      </MessageActionsContext.Provider>
    </MessageFeedContext.Provider>
  );
};
